import { Box, Chip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridValueFormatterParams } from '@mui/x-data-grid-premium';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { DeviceV3, Report } from '../../../backendsdk';
import { TrackedDataGrid as StyledDataGrid } from '../../../components/TrackedComponents';
import { ExtendedFleetDriver } from '../../../hooks/drivers';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { reportsPageDefs } from '../../../utils/Pages';
import { localeSort } from '../../../utils/Str';
import { daysNames, ordinalSuffix } from '../../../utils/TimeFormatter';
import palette from '../../ColorPalette';
import { DEFAULT_TITLE } from '../../Layout';
import { gridLocalization } from '../OTA/MuiDeviceTable';
import { REPORT_TYPE_TO_URL_PARAM } from './ReportComponent';
import ReportAlertDetailsComponent, {
    DEVICE_FILTER_OPTIONS,
    SCOPE_OPTIONS,
    deviceFilter,
    getExtraFilters,
} from './ReportDetails';
import { AlertReport, REVERSED_SEVERITY_FILTER_VALUES } from './ReportOptions';
import { ASSIGNEE_OPTIONS } from './ReportOptions';

interface ScheduledReportsComponentProps {
    type: 'REPORTS' | 'ALERTS';
    reports: AlertReport[];
    devices: DeviceV3[];
    drivers: ExtendedFleetDriver[];
    geofences: Record<string, string>;
    isLoading: boolean;
    onGenerate?: CallableFunction;
    setTab?: CallableFunction;
    disableType?: boolean;
    reportType: string;
}

const ScheduledReportsComponent: React.FC<ScheduledReportsComponentProps> = (props: ScheduledReportsComponentProps) => {
    const history = useHistory();
    const [selectedReport, setSelectedReport] = useState<AlertReport>();
    const { t, i18n } = useTranslation();
    const { profile } = useProfile();
    const isMobile = useIsMobile();
    const { alertId, reportId } = useParams<{ alertId: string; reportId: string }>();

    const isReports = props.type === 'REPORTS';

    useEffect(() => {
        document.title = `${DEFAULT_TITLE} | ${t(`navigator.${reportsPageDefs.name}`)}`;
    }, []);

    useEffect(() => {
        const relevantId = isReports ? reportId : alertId;
        if (relevantId && !isNaN(parseInt(relevantId))) {
            const report = props.reports.find((r) => r.pk === parseInt(relevantId));
            if (report) {
                setSelectedReport(report);
            }
        } else {
            setSelectedReport(undefined);
        }
    }, [props.reports, reportId, alertId]);

    const getScheduleType = (a: AlertReport) => {
        if (a.is_report) {
            const details = a as Report;
            return t(`content.reports.form.scope.${details.report_scope.toLocaleLowerCase()}`, details.report_scope);
        } else {
            return t('content.reports.alert');
        }
    };

    const getReportScope = (a: AlertReport) => {
        const details = a as Report;
        const currentDate = new Date();
        currentDate.setUTCHours(parseInt(details.report_time.slice(0, 2)));
        currentDate.setUTCMinutes(parseInt(details.report_time.slice(3, 5)));
        const formatInt = (i: number) => i.toString().padStart(2, '0');
        return `${
            details.report_scope != SCOPE_OPTIONS.DAILY
                ? `${t('time.every')} ${
                      details.report_scope == SCOPE_OPTIONS.WEEKLY
                          ? t(`time.day.${daysNames[details.report_day].toLocaleLowerCase()}`)
                          : `${i18n.languages[0] == 'en' ? ordinalSuffix(details.report_day) : details.report_day}`
                  }`
                : `${t('time.every')} ${t('time.day.header').toLocaleLowerCase()}`
        } ${t('time.at', { time: `${formatInt(currentDate.getHours())}:${formatInt(currentDate.getMinutes())}` })}`;
    };

    const formatScope = (a: AlertReport) => {
        if (a.is_report) {
            return `${getScheduleType(a)}: ${getReportScope(a)}`;
        } else {
            return getScheduleType(a);
        }
    };

    const renderScope = (a: AlertReport) => {
        if (a.is_report) {
            return (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ fontWeight: 'bold' }}>{getScheduleType(a)}</span>:&nbsp;
                    <span>{getReportScope(a)}</span>
                </div>
            );
        } else {
            return (
                <span style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {getScheduleType(a)}
                </span>
            );
        }
    };

    const getGroupedFilters = (tKey: string, key: string, values: Array<string>) => {
        const formattedKey = t(`content.reports.form.${tKey}.${key.toLocaleLowerCase()}`, key);
        if (values.length === 0) {
            return { key: formattedKey };
        } else if (values.length > 1) {
            return { key: `${values.length} ${t(`content.reports.form.${tKey}.${key.toLocaleLowerCase()}_plural`)}` };
        } else {
            return {
                key: formattedKey,
                value: t(`content.reports.form.filter.${values[0].toLocaleLowerCase()}`, values[0]),
            };
        }
    };

    const getUngroupedFilters = (tKey: string, key: string, values: Array<string> | string) => {
        let formattedValues;
        if (key != DEVICE_FILTER_OPTIONS.ALL) {
            if (key.toLocaleLowerCase() === 'assignee' && values.includes(ASSIGNEE_OPTIONS.CUSTOMER)) {
                formattedValues = [profile.customer.name];
            } else if (key.toLocaleLowerCase() === 'severity' && typeof values === 'string') {
                if (values in REVERSED_SEVERITY_FILTER_VALUES) {
                    const valueName =
                        REVERSED_SEVERITY_FILTER_VALUES[values as keyof typeof REVERSED_SEVERITY_FILTER_VALUES];
                    formattedValues = [t(`content.reports.form.filter.${valueName.toLocaleLowerCase()}`)];
                } else {
                    formattedValues = [values];
                }
            } else if (key.toLocaleLowerCase() === 'geofence' && values.length > 0) {
                formattedValues = (values as string[]).map((s: string) => props.geofences[s]);
            } else {
                formattedValues = (values as string[]).map((s: string) =>
                    t(`content.reports.form.filter.${s.toLocaleLowerCase()}`, s),
                );
            }
        }
        return {
            key: t(`content.reports.form.${tKey}.${key.toLocaleLowerCase()}`, key),
            values: formattedValues,
        };
    };

    const formatFilters = (tKey: string, key: string, values: Array<string> | string, groupValues = false) => {
        if (groupValues) {
            const res = getGroupedFilters(tKey, key, values as string[]);
            return res.value ? `${res.key}: ${res.value}` : res.key;
        }
        const res = getUngroupedFilters(tKey, key, values);
        return res.values ? `${res.key}: ${res.values.join(', ')}` : res.key;
    };

    const renderFilters = (tKey: string, key: string, values: Array<string> | string, groupValues = false) => {
        if (groupValues) {
            const res = getGroupedFilters(tKey, key, values as string[]);
            return (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{res.key}</span>
                    {res.value && <span>: {res.value}</span>}
                </div>
            );
        }
        const res = getUngroupedFilters(tKey, key, values);
        return (
            <Box key={key} sx={{ display: 'flex' }}>
                <span style={{ fontWeight: 'bold', paddingTop: '4px', whiteSpace: 'nowrap' }}>{res.key}</span>
                {res.values && (
                    <>
                        <span style={{ paddingTop: '4px', marginRight: '4px' }}>:</span>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            {res.values.map((value) => {
                                return (
                                    <Chip
                                        size="small"
                                        label={<Typography fontSize={12}>{value}</Typography>}
                                        key={value}
                                        sx={{ mr: 0.5, my: 0.25, cursor: 'pointer' }}
                                    />
                                );
                            })}
                        </Box>
                    </>
                )}
            </Box>
        );
    };

    const formatExtraFilters = (a: AlertReport) => {
        const filters = getExtraFilters(a);
        return filters.map(([key, values]) => formatFilters('extra_filters', key, values)).join('; ');
    };

    const renderExtraFilters = (a: AlertReport) => {
        const filters = getExtraFilters(a);
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {filters.map(([key, values]) => renderFilters('extra_filters', key, values))}
            </Box>
        );
    };

    const gridColumns: GridColDef[] = useMemo(() => {
        let columns;
        if (isReports) {
            columns = [
                {
                    field: 'time',
                    headerName: t('content.reports.columns.time'),
                    sortable: false,
                    filterable: false,
                    renderCell: (params: GridRenderCellParams) => renderScope(params.row.reportAlert),
                    flex: 1,
                },
                {
                    field: 'vehicles',
                    headerName: t('content.reports.columns.vehicles'),
                    sortable: false,
                    filterable: false,
                    renderCell: (params: GridRenderCellParams) =>
                        renderFilters('filter', ...deviceFilter(params.row.reportAlert.filters), true),
                    flex: 1,
                },
                {
                    field: 'rules',
                    headerName: t('content.reports.columns.rules'),
                    sortable: false,
                    renderCell: (params: GridRenderCellParams) => renderExtraFilters(params.row.reportAlert),
                    minWidth: 300,
                    flex: 1,
                },
                {
                    field: 'active',
                    headerName: t('content.reports.columns.active'),
                    type: 'boolean',
                    minWidth: 100,
                },
            ];
        } else {
            columns = [
                {
                    field: 'vehicles',
                    headerName: t('content.reports.columns.vehicles'),
                    sortable: false,
                    filterable: false,
                    renderCell: (params: GridRenderCellParams) =>
                        renderFilters('filter', ...deviceFilter(params.row.reportAlert.filters), true),
                    flex: 1,
                },
                {
                    field: 'rules',
                    headerName: t('content.reports.columns.rules'),
                    sortable: false,
                    renderCell: (params: GridRenderCellParams) => renderExtraFilters(params.row.reportAlert),
                    minWidth: 300,
                    flex: 1,
                },
                {
                    field: 'method',
                    headerName: t('content.reports.form.method.header'),
                    type: 'singleSelect',
                    valueOptions: ['EMAIL', 'SMS'].map((option) => {
                        return { value: option, label: t(`content.reports.form.method.${option.toLocaleLowerCase()}`) };
                    }),
                    valueFormatter: (params: GridValueFormatterParams) => {
                        return params.value
                            .split('_')
                            .map((v: string) => t(`content.reports.form.method.${v.toLocaleLowerCase()}`))
                            .sort(localeSort)
                            .join(', ');
                    },
                    minWidth: 150,
                },
                {
                    field: 'active',
                    headerName: t('content.reports.columns.active'),
                    type: 'boolean',
                    minWidth: 100,
                },
            ];
        }
        return columns.map((column) => ({
            ...column,
            renderHeader: (params) => (
                <Typography variant="overline" sx={{ fontWeight: 500 }}>
                    {params.colDef.headerName}
                </Typography>
            ),
        }));
    }, [props.geofences]);

    const rows = useMemo(() => {
        return props.reports.map((report) => {
            return {
                id: report.pk,
                type: report.report_type,
                time: formatScope(report),
                vehicles: formatFilters('filter', ...deviceFilter(report.filters), true),
                rules: formatExtraFilters(report),
                active: report.active,
                method: 'method' in report ? report.method : 'EMAIL',
                reportAlert: report,
            };
        });
    }, [props.reports, props.geofences]);

    return (
        <>
            <StyledDataGrid
                id="reports-data-grid"
                data-testid="reports-data-grid"
                loading={props.isLoading}
                getRowHeight={() => 'auto'}
                columns={gridColumns}
                rows={rows}
                disableRowSelectionOnClick
                hideFooter
                disableVirtualization={isMobile || process.env.JEST_WORKER_ID !== undefined}
                getCellClassName={(params) => `${params.field}-cell`}
                onRowClick={(params) => {
                    setSelectedReport(params.row.reportAlert);
                    history.push(
                        `/${props.type.toLocaleLowerCase()}/${REPORT_TYPE_TO_URL_PARAM[props.reportType]}/${
                            params.row.id
                        }`,
                    );
                }}
                slots={{
                    noRowsOverlay: () => (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="overline">
                                {t(`content.reports.no_scheduled_${props.type.toLocaleLowerCase()}`)}
                            </Typography>
                        </Box>
                    ),
                }}
                slotProps={{
                    columnsPanel: {
                        sx: {
                            '& .MuiDataGrid-panelFooter button:first-child': {
                                display: 'none',
                            },
                        },
                    },
                }}
                localeText={{
                    ...gridLocalization[i18n.languages[0]],
                    filterPanelRemoveAll: t('table.remove_all'),
                    columnMenuManageColumns: t('table.manage_columns'),
                    unpin: t('table.unpin'),
                }}
                columnHeaderHeight={42}
                sx={{
                    height: '100%',
                    width: '100%',
                    pb: 2,
                    '& .MuiDataGrid-main': {
                        borderRadius: '4px',
                        boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.25)',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderBottom: 'none',
                        color: palette.neutral[100],
                        '& .MuiSvgIcon-root': {
                            fill: palette.neutral[100],
                        },
                    },
                    '& .MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                    },
                    '& .MuiDataGrid-cell--editing': {
                        padding: '0 !important',
                    },
                    '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-row--dynamicHeight': { minHeight: '52px !important' },
                    '&.MuiDataGrid-root .MuiDataGrid-cell': { py: '6px' },
                    backgroundColor: palette.neutral[50],
                    minHeight: 0,
                }}
            />
            {selectedReport !== undefined ? (
                <ReportAlertDetailsComponent
                    type={props.type}
                    details={selectedReport}
                    devices={props.devices}
                    geofences={props.geofences}
                    onClose={() => {
                        setSelectedReport(undefined);
                        history.push(
                            `/${props.type.toLocaleLowerCase()}/${REPORT_TYPE_TO_URL_PARAM[props.reportType]}`,
                        );
                    }}
                    onGenerate={props.onGenerate}
                />
            ) : null}
        </>
    );
};

export default ScheduledReportsComponent;
